import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import useIsMobile from '../../shared/hooks/useIsMobile';
import { ALL_COLUMNS, DEFAULT_LAST_SHOW, HISTORY_OPTIONS, LAST_SHOW_OPTIONS, SLIDERS, STATUS_OPTIONS } from '../constants';
import useArtistBrowse from '../useArtistBrowse';
import { formatNumber } from '../utils';
import ArtistCards from './ArtistCards';
import ArtistTable from './ArtistTable';
import FacetRail from './FacetRail';
import FiltersSheet from './FiltersSheet';
import SmartSuggestions from './SmartSuggestions';
import SortControl from './SortControl';
import TableSetupPopover from './TableSetupPopover';

const META = Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, c]));

const activeChips = (filters, setFilter) => {
  const chips = [];
  filters.genre.forEach((g) => chips.push({ key: `genre:${g}`, label: g, onRemove: () => setFilter({ genre: filters.genre.filter((x) => x !== g) }) }));
  filters.agency.forEach((a) => chips.push({ key: `agency:${a}`, label: a, onRemove: () => setFilter({ agency: filters.agency.filter((x) => x !== a) }) }));
  filters.tag.forEach((t) => chips.push({ key: `tag:${t}`, label: t, onRemove: () => setFilter({ tag: filters.tag.filter((x) => x !== t) }) }));
  if (filters.history.length === 1) {
    const opt = HISTORY_OPTIONS.find((o) => o.key === filters.history[0]);
    if (opt) chips.push({ key: 'history', label: `History: ${opt.label}`, onRemove: () => setFilter({ history: HISTORY_OPTIONS.map((o) => o.key) }) });
  }
  if (filters.status.length === 1) {
    const opt = STATUS_OPTIONS.find((o) => o.key === filters.status[0]);
    if (opt) chips.push({ key: 'status', label: opt.label, onRemove: () => setFilter({ status: STATUS_OPTIONS.map((o) => o.key) }) });
  }
  if (filters.lastShow !== DEFAULT_LAST_SHOW) {
    const opt = LAST_SHOW_OPTIONS.find((o) => o.key === filters.lastShow);
    if (opt) chips.push({ key: 'lastShow', label: `Last performed: ${opt.label}`, onRemove: () => setFilter({ lastShow: DEFAULT_LAST_SHOW }) });
  }
  SLIDERS.forEach((s) => {
    if (filters[s.key]) {
      chips.push({
        key: s.key,
        label: `${s.title}: ${formatNumber(filters[s.key][0])}–${formatNumber(filters[s.key][1])}`,
        onRemove: () => setFilter({ [s.key]: null }),
      });
    }
  });
  if (filters.hasMetrics) chips.push({ key: 'metrics', label: 'Has metrics', onRemove: () => setFilter({ hasMetrics: false }) });
  return chips;
};

const ArtistsBrowse = ({ dataUrl, facetOptionsUrl, suggestionsUrl }) => {
  const browse = useArtistBrowse(dataUrl);
  const {
    filters, facets, rows, total, loading, error, hasMore, columns, view,
    setFilter, setColumns, resetFilters, loadMore, reload, setView,
  } = browse;

  const isMobile = useIsMobile(768);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [query, setQuery] = useState(filters.q);
  useEffect(() => {
    const id = setTimeout(() => { if (query !== filters.q) setFilter({ q: query }); }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  // Keep the search box in sync when q is cleared elsewhere (e.g. Clear all).
  useEffect(() => { setQuery(filters.q); }, [filters.q]);

  const setSort = (sort, dir) => setFilter({ sort, dir });
  const visibleColumns = columns.filter((c) => c.visible).map((c) => c.key);
  const chips = activeChips(filters, setFilter);
  const sortLabel = META[ALL_COLUMNS.find((c) => c.sortKey === filters.sort)?.key || 'spotify']?.label || 'Spotify';

  const searchInput = (
    <div className="ab__search">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search artists, agents"
        aria-label="Search artists, agents"
      />
    </div>
  );

  const summary = (
    <div className="ab__summary">
      <div className="ab__count">
        <span>{formatNumber(total)} artists</span>
        {chips.map((chip) => (
          <button type="button" key={chip.key} className="ab-filterchip" onClick={chip.onRemove}>
            {chip.label}<span className="ab-filterchip__x">✕</span>
          </button>
        ))}
      </div>
      <div className="ab__sortsummary">Sorted by {sortLabel} {filters.dir === 'asc' ? '↑' : '↓'}</div>
    </div>
  );

  if (isMobile) {
    // ponytail: add the Smart suggestions toggle to mobile when a booker asks for it.
    return (
      <div className="ab ab--mobile">
        <div className="ab__panel">
          <div className="ab__main">
            <div className="ab__toolbar">
              {searchInput}
              <div className="ab-mtoolbar">
                <button type="button" className="ab-mfilters" onClick={() => setFiltersOpen(true)}>
                  Filters{chips.length > 0 && <span className="ab-badge">{chips.length}</span>}
                </button>
                <SortControl sort={filters.sort} dir={filters.dir} setSort={setSort} />
              </div>
              {summary}
            </div>
            <ArtistCards
              rows={rows}
              loading={loading}
              error={error}
              onRetry={reload}
              hasMore={hasMore}
              loadMore={loadMore}
            />
          </div>
        </div>
        {filtersOpen && (
          <FiltersSheet onClose={() => setFiltersOpen(false)} onClearAll={resetFilters}>
            <FacetRail filters={filters} facets={facets} setFilter={setFilter} facetOptionsUrl={facetOptionsUrl} />
          </FiltersSheet>
        )}
      </div>
    );
  }

  return (
    <div className="ab">
      <div className="ab__panel">
        <FacetRail filters={filters} facets={facets} setFilter={setFilter} facetOptionsUrl={facetOptionsUrl} view={view} />

        <div className="ab__main">
          <div className="ab__toolbar">
            <div className="ab-modes" role="tablist">
              <button type="button" role="tab" aria-selected={view === 'all'}
                className={`ab-mode ${view === 'all' ? 'is-active' : ''}`} onClick={() => setView('all')}>
                All artists
              </button>
              <button type="button" role="tab" aria-selected={view === 'suggestions'}
                className={`ab-mode ${view === 'suggestions' ? 'is-active' : ''}`} onClick={() => setView('suggestions')}>
                <span aria-hidden="true">✦ </span>Smart suggestions
              </button>
            </div>
            <div className="ab__searchrow">
              {searchInput}
              {view === 'all' && (
                <div className="ab__actions">
                  <TableSetupPopover
                    columns={columns}
                    setColumns={setColumns}
                    sort={filters.sort}
                    dir={filters.dir}
                    setSort={setSort}
                  />
                </div>
              )}
            </div>
            {view === 'all' && summary}
          </div>

          {view === 'suggestions' ? (
            <SmartSuggestions suggestionsUrl={suggestionsUrl} filters={filters} />
          ) : (
            <ArtistTable
              rows={rows}
              visibleColumns={visibleColumns}
              sort={filters.sort}
              dir={filters.dir}
              setSort={setSort}
              loading={loading}
              error={error}
              onRetry={reload}
              hasMore={hasMore}
              loadMore={loadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
};
ArtistsBrowse.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  facetOptionsUrl: PropTypes.string.isRequired,
  suggestionsUrl: PropTypes.string.isRequired,
};

export default ArtistsBrowse;
